import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * Shared Button primitive — first entry in the `components/ui/`
 * design-system layer (shadcn/ui conventions on the Radix + CVA +
 * tailwind-merge stack that was already installed; see
 * `docs/design/design-system.md`).
 *
 * Variants are derived from the app's EXISTING visual language, not
 * imported from shadcn's defaults:
 *
 *   - `default`   — accent-filled action (crucible-accent tokens).
 *   - `secondary` — the `border-white/[0.12] bg-white/[0.06]` chip
 *                   pattern used across the new-UI workspace
 *                   (e.g. the phone-cap "Change" control).
 *   - `ghost`     — borderless hover-wash row/menu actions.
 *   - `glass`     — the floating blurred pill used by the overlay
 *                   action clusters over the preview (Back / Reload /
 *                   Restart in `PreviewOverlay`).
 *
 * `app-region-no-drag` is baked into the base class: every button in
 * the Electron shell must opt out of drag-region hit-testing or
 * clicks get eaten by the window-drag region (issues #531 / #804).
 * Harmless in non-Electron consumers (crucible-web).
 */
const buttonVariants = cva(
  // Standard button shape is FULLY ROUNDED: text buttons read as pills,
  // and the `icon` size becomes a circle. `rounded-full` lives in the base
  // class so it's the system default, not a per-call-site override.
  // `aria-disabled:` mirrors the `disabled:` dim/cursor treatment WITHOUT
  // `pointer-events-none` — call sites use `aria-disabled` (not the native
  // attribute) when a gated control must stay hoverable so its peer-hover
  // gating tooltip still fires (#2406). Per-variant `aria-disabled:hover:*`
  // below neutralises the interactive hover affordance so a gated control
  // never brightens like an enabled one.
  'app-region-no-drag inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full font-medium transition-[transform,color,background-color,border-color,opacity] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crucible-accent/60 active:scale-[0.97] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40 aria-disabled:cursor-not-allowed aria-disabled:opacity-40',
  {
    variants: {
      variant: {
        // `bg-crucible-accent` is the solid fallback (legacy + light themes);
        // `.brand-gradient-fill` layers the Canary → Clementine gradient on
        // top in dark new-UI. Text colour comes from the brand-contrast rule
        // in globals.css (dark ink on the bright fill).
        default:
          'brand-gradient-fill bg-crucible-accent text-white hover:bg-crucible-accent-hover aria-disabled:hover:bg-crucible-accent',
        secondary:
          'border border-white/[0.12] bg-white/[0.06] text-white/72 hover:border-white/[0.24] hover:text-white aria-disabled:hover:border-white/[0.12] aria-disabled:hover:text-white/72',
        ghost:
          'text-white/72 hover:bg-white/[0.08] hover:text-white aria-disabled:hover:bg-transparent aria-disabled:hover:text-white/72',
        glass:
          'border border-white/[0.08] bg-[rgba(20,16,26,0.72)] text-[#f1ecf7] shadow-[0_6px_18px_rgba(0,0,0,0.35)] backdrop-blur-[8px]',
      },
      size: {
        default: 'px-4 py-2 text-[13px]',
        sm: 'px-3 py-1 text-[11px]',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Render the child element with button classes/behaviour merged in
   *  (Radix `Slot`), e.g. styling an `<a>` like a button. */
  asChild?: boolean;
  /**
   * First-class "gated" affordance (#2504). Use this — NOT a hand-rolled
   * `aria-disabled={cond || undefined}` + a manually-guarded `onClick` —
   * whenever a control is unavailable BUT must stay hoverable so a sibling
   * peer-hover Tooltip carrying the gating reason still fires (the #2406
   * pattern). When `gated` is true the Button:
   *
   *   - sets `aria-disabled` (dim/cursor treatment via the base
   *     `aria-disabled:*` classes — same look as native `disabled`);
   *   - does NOT set the native `disabled` attribute, so the element stays
   *     in the pointer flow and keeps emitting hover/focus events (a
   *     natively-disabled element emits none, which kills peer-hover +
   *     the gating tooltip — the HIGH-severity #2406 regression);
   *   - INTERNALLY no-ops `onClick` and keyboard activation (Enter / Space),
   *     so the action CANNOT fire even though the click handler is still
   *     wired. Call sites can no longer get the guard half-right.
   *
   * For the no-tooltip case keep using the native `disabled` prop, which
   * bakes `disabled:pointer-events-none` and is the simpler correct choice.
   */
  gated?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      type,
      gated = false,
      onClick,
      onKeyDown,
      'aria-disabled': ariaDisabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    // When gated, swallow activation internally so the guard is centralized
    // and cannot be forgotten at a call site. We keep the element hoverable
    // (no native `disabled`, no `pointer-events-none`) so a peer-hover
    // tooltip still fires; we just refuse to run the action.
    const handleClick = gated
      ? (event: React.MouseEvent<HTMLButtonElement>): void => {
          event.preventDefault();
          event.stopPropagation();
        }
      : onClick;
    const handleKeyDown = gated
      ? (event: React.KeyboardEvent<HTMLButtonElement>): void => {
          // Block keyboard activation (Enter / Space) but preserve every
          // other key (focus/Tab, arrow navigation, etc.) so the control
          // stays reachable for assistive tech.
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          onKeyDown?.(event);
        }
      : onKeyDown;

    return (
      <Comp
        ref={ref}
        // Default to type="button" — bare <button> defaults to
        // "submit" and fires form submission when nested in a form.
        // Slot targets may not accept `type`, so only set it on the
        // real element.
        {...(asChild ? {} : { type: type ?? 'button' })}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
        // These sit AFTER the `...props` spread so a stray caller-supplied
        // value can never override the gated guard.
        //
        // `aria-disabled` (never the native `disabled`) is how a gated
        // control keeps its dim look while staying hoverable — see the
        // `gated` prop docs. A caller may still pass `aria-disabled`
        // directly for non-Button-gated cases; `gated` wins when set.
        aria-disabled={gated || ariaDisabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
